/**
 * Automation curves (2026-10-16, record mode's "fader moves"): pure helpers shared by the timeline editor and its
 * checks. An automation lane's points ramp (`linear`) or hold-then-jump (`hold`) to the next; before the first point
 * the curve sits at the first value, after the last it holds (as `bridgeSync.ts::automationCues` plays it).
 */

import type { AutomationPointStep } from '@opbuttons/schema';

/** How far along a ramp of this shape is at `q` (0..1 of the way in time), 0..1 of the way in value. The SAME maths
 *  as OP-Motion's engine (OPM4LBuilder `staging/op-motion-gen.mjs`, 2026-10-23), so a curve drawn here is the curve
 *  the device plays: S-curve `0.5 - 0.5 cos(pi q)`, riser `q^2`, fall-away `1 - (1 - q)^2`, straight `q`, late cut an
 *  S-curve in the last fifth. `hold` stays put until the next point. */
export function shapeAt(curve: AutomationPointStep['curve'], q: number): number {
  const x = Math.max(0, Math.min(1, q));
  switch (curve ?? 'linear') {
    case 's': return 0.5 - 0.5 * Math.cos(Math.PI * x);
    case 'riser': return x * x;
    case 'fall': return 1 - (1 - x) * (1 - x);
    case 'late': { const y = Math.max(0, Math.min(1, (x - 0.8) / 0.2)); return 0.5 - 0.5 * Math.cos(Math.PI * y); }
    case 'hold': return x >= 1 ? 1 : 0;
    default: return x;
  }
}

/** The curve's value at `bar`, unrounded (null with no points): before the first point it sits at the first value,
 *  after the last it holds. */
export function autoValueAtExact(pts: readonly AutomationPointStep[], bar: number): number | null {
  if (pts.length === 0) return null;
  if (bar <= pts[0]!.position) return pts[0]!.value;
  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i]!; const b = pts[i + 1]!;
    if (bar >= b.position) continue;
    if ((a.curve ?? 'linear') === 'hold' || b.position <= a.position) return a.value;
    return a.value + (b.value - a.value) * shapeAt(a.curve, (bar - a.position) / (b.position - a.position));
  }
  return pts[pts.length - 1]!.value;
}

/** The curve's value at `bar` (null with no points), as the compile plays it: a 7-bit value. */
export function autoValueAt(pts: readonly AutomationPointStep[], bar: number): number | null {
  const v = autoValueAtExact(pts, bar);
  return v === null ? null : Math.round(v);
}
/** A recorded run of values as the fewest points whose straight lines stay within `tol` of every value sent
 *  (Ramer-Douglas-Peucker on the value error). `run` is in time order. */
export function thinRun(run: ReadonlyArray<{ bar: number; value: number }>, tol = 1): Array<{ bar: number; value: number }> {
  if (run.length <= 2) return [...run];
  const keep = new Array<boolean>(run.length).fill(false);
  keep[0] = true; keep[run.length - 1] = true;
  const stack: Array<[number, number]> = [[0, run.length - 1]];
  while (stack.length) {
    const [i, j] = stack.pop()!;
    const a = run[i]!; const b = run[j]!;
    let worst = -1; let at = -1;
    for (let k = i + 1; k < j; k++) {
      const c = run[k]!;
      const line = b.bar > a.bar ? a.value + ((b.value - a.value) * (c.bar - a.bar)) / (b.bar - a.bar) : a.value;
      const err = Math.abs(c.value - line);
      if (err > worst) { worst = err; at = k; }
    }
    if (worst > tol && at > 0) { keep[at] = true; stack.push([i, at], [at, j]); }
  }
  return run.filter((_, k) => keep[k]);
}
